import { useState, useCallback, useMemo, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { RowClassParams } from 'ag-grid-community'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { ExternalServiceApiTokenRow } from './ExternalServiceApiTokenRow'
import { useGridOptions } from './gridOptions/useGridOptions'
import { createGetContextMenuItems } from './gridOptions/contextMenu'
import { useExternalServiceApiTokenData } from './hooks/useExternalServiceApiTokenData'
import ExternalServiceApiTokensHeader from './components/Header'
import CreateTokenDialog from './components/CreateTokenDialog'
import TokenDisplayModal from './components/TokenDisplayModal'
import RevokeConfirmDialog from './components/RevokeConfirmDialog'
import Loading from '../../components/process-state-notifications/Loading'
import { ExternalServiceApiTokenScope } from '../../../domain/entity/ExternalServiceApiTokenEntity'
import { colorPalette } from '../../style/colorPalette'
import { PageArea } from '..'

type Props = {
  uuid: string
}

const ExternalServiceApiTokens = ({ uuid: functionUuid }: Props) => {
  const bulkSheetRef = useRef<HTMLDivElement>(null)
  const gridRef = useRef<AgGridReact<ExternalServiceApiTokenRow>>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [tokenModalOpen, setTokenModalOpen] = useState(false)
  const [displayedToken, setDisplayedToken] = useState<string | null>(null)
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [revokeTarget, setRevokeTarget] =
    useState<ExternalServiceApiTokenRow | null>(null)

  const { rowData, loading, onReload, onCreateToken, onRevokeToken } =
    useExternalServiceApiTokenData(functionUuid)

  const gridOptions = useGridOptions()

  const onRevokeClick = useCallback((row: ExternalServiceApiTokenRow) => {
    setRevokeTarget(row)
    setRevokeDialogOpen(true)
  }, [])

  const onConfirmRevoke = useCallback(async () => {
    if (!revokeTarget) return
    const success = await onRevokeToken(revokeTarget.uuid)
    if (success) {
      setRevokeDialogOpen(false)
      setRevokeTarget(null)
    }
  }, [revokeTarget, onRevokeToken])

  const onCancelRevoke = useCallback(() => {
    setRevokeDialogOpen(false)
    setRevokeTarget(null)
  }, [])

  const getContextMenuItems = useMemo(
    () => createGetContextMenuItems(onRevokeClick),
    [onRevokeClick]
  )

  const onOpenCreateDialog = useCallback(() => {
    setCreateDialogOpen(true)
  }, [])

  const onCloseCreateDialog = useCallback(() => {
    setCreateDialogOpen(false)
  }, [])

  const onSubmitCreate = useCallback(
    async ({
      serviceName,
      expiredAt,
      scopes,
    }: {
      serviceName: string
      expiredAt: number
      scopes: ExternalServiceApiTokenScope[]
    }) => {
      const rawToken = await onCreateToken({ serviceName, expiredAt, scopes })
      if (rawToken) {
        setCreateDialogOpen(false)
        setDisplayedToken(rawToken)
        setTokenModalOpen(true)
      }
    },
    [onCreateToken]
  )

  const onCloseTokenModal = useCallback(() => {
    setTokenModalOpen(false)
    setDisplayedToken(null)
  }, [])

  const getRowStyle = useCallback(
    ({ data }: RowClassParams<ExternalServiceApiTokenRow>) => {
      if (data?.revoked || data?.expired) {
        return { backgroundColor: colorPalette.monotone[2] }
      }
      return undefined
    },
    []
  )

  return (
    <PageArea>
      <ExternalServiceApiTokensHeader
        loading={loading}
        onReload={onReload}
        onClickCreateToken={onOpenCreateDialog}
      />
      <BulkSheetView
        ref={bulkSheetRef}
        gridRef={gridRef}
        gridOptions={gridOptions}
        rowData={rowData}
        getContextMenuItems={getContextMenuItems}
        getRowStyle={getRowStyle}
      />
      <Loading isLoading={loading} elem={bulkSheetRef.current} />
      <CreateTokenDialog
        open={createDialogOpen}
        onClose={onCloseCreateDialog}
        onSubmit={onSubmitCreate}
        loading={loading}
      />
      <TokenDisplayModal
        open={tokenModalOpen}
        rawToken={displayedToken}
        onClose={onCloseTokenModal}
      />
      <RevokeConfirmDialog
        open={revokeDialogOpen}
        serviceName={revokeTarget?.serviceName ?? ''}
        onConfirm={onConfirmRevoke}
        onCancel={onCancelRevoke}
        loading={loading}
      />
    </PageArea>
  )
}

export default ExternalServiceApiTokens
